import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCameraAlt, MdCheckCircle, MdError, MdUpload, MdDelete, MdFlipCameraIos, MdClose } from 'react-icons/md';
import api from '../api/axios';

const InspectionItem = ({ label, field, value, onCapture, onRetake }) => {
    return (
        <div className="bg-white p-3 md:p-4 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition-all flex flex-col h-full">
            <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-gray-700 text-[10px] md:text-xs uppercase tracking-tight truncate w-full pr-2" title={label}>{label}</h3>
                {value ? (
                    <span className="shrink-0 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-medium border border-green-200">
                        <MdCheckCircle /> Captured
                    </span>
                ) : (
                    <span className="shrink-0 bg-red-50 text-red-500 text-[10px] md:text-xs px-2 py-1 rounded-full flex items-center gap-1 font-medium border border-red-100">
                        <MdError /> Required
                    </span>
                )}
            </div>

            <div className="relative w-full shadow-inner bg-gray-50 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex flex-col items-center justify-center group flex-grow min-h-[180px] hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => !value && onCapture(field)}>
                {value ? (
                    <>
                        <img src={URL.createObjectURL(value)} alt={label} className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRetake(field); }}
                                className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 hover:bg-red-600 transition-transform hover:scale-105 shadow-xl"
                            >
                                <MdDelete size={16} /> Retake Photo
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center gap-3 text-gray-400 group-hover:text-blue-500 transition-colors p-4 text-center">
                        <div className="w-14 h-14 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <MdCameraAlt size={26} />
                        </div>
                        <span className="text-sm font-semibold">Tap to Capture<br /><span className="text-xs font-normal opacity-70">Camera will open</span></span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function CenterInspectionForm() {
    const [photos, setPhotos] = useState({
        photo_classroom: null,
        photo_bathroom: null,
        photo_boys_washroom: null,
        photo_hall: null,
        photo_cctv: null,
        photo_cctv_live: null,
        photo_computer_lab: null,
        photo_dustbin: null,
        photo_fire_safety: null,
        photo_notice_board: null,
        photo_water_jar: null,
        photo_electric_bill: null
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Admin Remark State
    const [latestReport, setLatestReport] = useState(null);
    const [adminRemark, setAdminRemark] = useState(null);

    // Fetch Latest Inspection on Mount
    useEffect(() => {
        const fetchLatest = async () => {
            try {
                const res = await api.get('/inspections/latest');
                if (res.data) {
                    setLatestReport(res.data);
                    if (res.data.admin_remarks) {
                        setAdminRemark(res.data.admin_remarks);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch latest inspection", err);
            }
        };
        fetchLatest();
    }, []);

    const handleDismissRemark = async () => {
        if (!latestReport || !latestReport.id) return;

        try {
            await api.delete(`/inspections/${latestReport.id}/admin-remarks`);
            setAdminRemark(null); // Clear from UI immediately
        } catch (err) {
            alert('Failed to dismiss remark');
        }
    };

    // Camera State
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [activeField, setActiveField] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');

    const items = [
        { label: "Classroom Area", field: "photo_classroom" },
        { label: "Computer Lab (Overview)", field: "photo_computer_lab" },
        { label: "CCTV Monitor Screen", field: "photo_cctv" },
        { label: "CCTV Live Time", field: "photo_cctv_live" },
        { label: "Hall / Reception", field: "photo_hall" },
        { label: "Girls Washroom", field: "photo_bathroom" },
        { label: "Boys Washroom", field: "photo_boys_washroom" },
        { label: "Dustbin Placement", field: "photo_dustbin" },
        { label: "Fire Safety / Water", field: "photo_fire_safety" },
        { label: "Notice Board", field: "photo_notice_board" },
        { label: "Water Jar / Dispenser", field: "photo_water_jar" },
        { label: "Electric Bill", field: "photo_electric_bill" },
    ];

    const startCamera = async (field) => {
        setActiveField(field);
        setIsCameraOpen(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: facingMode }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Camera Error:", err);
            setError("Could not access camera. Please allow permissions.");
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        setIsCameraOpen(false);
        setActiveField(null);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Resize Logic: Max 1024px
            const MAX_SIZE = 1024;
            let width = video.videoWidth;
            let height = video.videoHeight;

            if (width > height) {
                if (width > MAX_SIZE) {
                    height *= MAX_SIZE / width;
                    width = MAX_SIZE;
                }
            } else {
                if (height > MAX_SIZE) {
                    width *= MAX_SIZE / height;
                    height = MAX_SIZE;
                }
            }

            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, width, height);

            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `${activeField}_${Date.now()}.jpg`, { type: 'image/jpeg' });
                    setPhotos(prev => ({ ...prev, [activeField]: file }));
                    stopCamera();
                }
            }, 'image/jpeg', 0.7); // Reduce quality to 0.7
        }
    };

    const switchCamera = () => {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    };

    // Re-initialize camera when facing mode changes
    useEffect(() => {
        if (isCameraOpen && activeField) {
            // 1. Stop existing tracks
            if (videoRef.current && videoRef.current.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            }

            // 2. Restart with new mode
            navigator.mediaDevices.getUserMedia({
                video: { facingMode: facingMode }
            }).then(stream => {
                if (videoRef.current) videoRef.current.srcObject = stream;
            }).catch(err => {
                console.error("Camera switch failed", err);
                setError("Camera switch failed or not supported.");
            });
        }
    }, [facingMode]); // Intentionally verify this dependency triggers re-run

    const handleSubmit = async () => {
        // Validate
        const missing = Object.entries(photos).filter(([_, val]) => !val).map(([key]) => key);
        if (missing.length > 0) {
            alert(`Please capture all required photos. Missing: ${missing.length} photos.`);
            return;
        }

        setLoading(true);
        setError('');

        const formData = new FormData();
        formData.append('report_date', new Date().toISOString().split('T')[0]);
        Object.keys(photos).forEach(key => {
            formData.append(key, photos[key]);
        });
        formData.append('remarks', 'Daily Inspection');

        try {
            await api.post('/inspections', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(true);
            setPhotos({
                photo_classroom: null, photo_bathroom: null, photo_boys_washroom: null, photo_hall: null,
                photo_cctv: null, photo_cctv_live: null, photo_computer_lab: null,
                photo_dustbin: null, photo_fire_safety: null,
                photo_notice_board: null, photo_water_jar: null, photo_electric_bill: null
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit inspection report.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg border border-green-100">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MdCheckCircle size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Inspection Submitted!</h2>
                <p className="text-gray-600 mb-6">Thank you for submitting today's center inspection report.</p>
                <button
                    onClick={() => setSuccess(false)}
                    className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700"
                >
                    Submit Another
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 pb-20 md:pb-0">
            <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-600 to-blue-800 rounded-xl p-4 md:p-6 text-white shadow-xl">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <MdCameraAlt size={80} className="md:w-32 md:h-32" />
                </div>
                <div className="relative z-10">
                    <h1 className="text-lg md:text-xl font-bold flex items-center gap-2 mb-2">
                        <div className="p-1.5 md:p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                            <MdCameraAlt size={20} className="md:w-6 md:h-6" />
                        </div>
                        Center Facility Inspection
                    </h1>
                    <p className="text-blue-100 text-xs md:text-sm max-w-xl opacity-90 leading-relaxed">
                        Strict compliance required. Capture live photos of all facility areas below. Old photos are not allowed and will be rejected.
                    </p>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg flex items-center gap-3 text-red-700">
                    <MdError size={24} />
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {items.map(item => (
                    <InspectionItem
                        key={item.field}
                        {...item}
                        value={photos[item.field]}
                        onCapture={startCamera}
                        onRetake={(f) => setPhotos(prev => ({ ...prev, [f]: null }))}
                    />
                ))}
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:static md:bg-transparent md:border-0 md:shadow-none md:p-0 mt-8 flex justify-end">
                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`
                        w-full md:w-auto px-6 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all
                        ${loading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:scale-105 hover:shadow-green-500/30 active:scale-95'
                        }
                    `}
                >
                    {loading ? 'Uploading...' : 'Submit Report'}
                </button>
            </div>

            {/* Camera Overlay */}
            <AnimatePresence>
                {isCameraOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black flex flex-col"
                    >
                        <div className="relative flex-1 bg-black overflow-hidden flex items-center justify-center">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />

                            {/* Camera UI Overlays */}
                            <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start bg-gradient-to-b from-black/50 to-transparent">
                                <button onClick={stopCamera} className="p-2 bg-black/20 backdrop-blur-md rounded-full text-white">
                                    <MdClose size={24} />
                                </button>
                                <div className="px-3 py-1 bg-black/40 backdrop-blur-md rounded-full text-white text-sm font-medium">
                                    {items.find(i => i.field === activeField)?.label}
                                </div>
                                <button onClick={switchCamera} className="p-2 bg-black/20 backdrop-blur-md rounded-full text-white">
                                    <MdFlipCameraIos size={24} />
                                </button>
                            </div>

                            {/* Capture Button */}
                            <div className="absolute bottom-0 left-0 right-0 p-8 flex justify-center bg-gradient-to-t from-black/50 to-transparent">
                                <button
                                    onClick={capturePhoto}
                                    className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                                >
                                    <div className="w-16 h-16 bg-white rounded-full"></div>
                                </button>
                            </div>
                        </div>
                        <canvas ref={canvasRef} className="hidden" />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
